import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormGroup, FormControl, Validators, Form} from '@angular/forms';
import {ThingSettingsInterface} from '@app/app/shared/interfaces/thing/thing-settings.interface';
import * as moment from 'moment';
import {CommandsService} from '@app/app/shared/services/commands.service';

@Component({
  templateUrl: './park-settings-form.html',
  selector: 'park-settings-form'
})
export class ParkSettingsFormComponent implements OnInit {
  @Input() public settings: ThingSettingsInterface;
  @Input() public parkId: string;
  @Input() public id: string;
  @Input() getSearchStatus: boolean;
  @Output() getSearchStatusChange = new EventEmitter<boolean>();
  public settingsForm: FormGroup;
  public supportedLanguages = [
    {code: 'en', name: 'English'},
    {code: 'fr', name: 'French'},
    {code: 'es', name: 'Spanish'},
    {code: 'ru', name: 'Russian'},
    {code: 'de', name: 'German'},
  ];
  inProgress = false;

  constructor(private comm: CommandsService) {
    this.settingsForm = new FormGroup({
      water_circuit: new FormGroup({
        any_fs_alarm_delay: new FormControl('', Validators.required),
        bypass_delay: new FormControl('', Validators.required),
        ch_alarm_delay: new FormControl('', Validators.required),
        elevation_delay: new FormControl('', Validators.required),
        rain_diverter_delay: new FormControl('', Validators.required),
        step_speed_delay: new FormControl('', Validators.required),
        uv_alarm_delay: new FormControl('', Validators.required),
        feature_pump_on_delay: new FormControl('', Validators.required),
        feature_fs_start_delay: new FormControl('', Validators.required),
        filter_fs_alarm_delay: new FormControl('', Validators.required),
        filter_fs_start_delay: new FormControl('', Validators.required),
        max_fast_valves_per_sec: new FormControl('', Validators.required),
        max_normal_valves_per_sec: new FormControl('', Validators.required),
        ch_empty_acid: new FormControl('', Validators.required),
        ch_full_acid: new FormControl('', Validators.required),
        ch_empty_chlorine: new FormControl('', Validators.required),
        ch_full_chlorine: new FormControl('', Validators.required),
        max_conso: new FormControl('', [Validators.required, Validators.min(0), Validators.max(100)]),
        mode: new FormControl('', Validators.required),
        sand_clean_dp: new FormControl('', Validators.required),
        sand_dirty_dp: new FormControl('', Validators.required),
        wind_max_speed: new FormControl('', Validators.required),
        wind_mid_speed: new FormControl('', Validators.required),
        y_clean_dp: new FormControl('', Validators.required),
        y_dirty_dp: new FormControl('', Validators.required),
      }),
      purge: new FormGroup({
        duration: new FormControl('', Validators.required),
        inactive_period: new FormControl('', Validators.required),
        hour: new FormControl('', Validators.required),
        rain_diverter: new FormControl('', Validators.required),
        auto: new FormControl('', Validators.required),
        bypass: new FormControl('', Validators.required),
      }),
      backwash: new FormGroup({
        duration: new FormControl('', Validators.required),
        time: new FormControl('', Validators.required),
        rain_diverter: new FormControl('', Validators.required),
        bypass: new FormControl('', Validators.required),
        auto: new FormControl('', Validators.required),
      }),
      splashpad: new FormGroup({
        auto_startup: new FormControl('', Validators.required),
      }),
      regional: new FormGroup({
        language: new FormControl('', Validators.required),
        units: new FormControl('', Validators.required),
        time_format: new FormControl('', Validators.required),
      }),
      led: new FormGroup({
        max_level: new FormControl('', Validators.required),
        step_level: new FormControl('', Validators.required),
        step_delay_ticks: new FormControl('', Validators.required),
      }),
      communication: new FormGroup({
        dhcp_on: new FormControl('', Validators.required),
        wlan_type: new FormControl('', Validators.required),
        ch_ip_address: new FormControl(''),
        maestro_ip_address: new FormControl(''),
        gsm_apn: new FormControl(''),
        gsm_pin: new FormControl(''),
        gsm_user: new FormControl(''),
        gsm_password: new FormControl(''),
      })
    });
  }

  public setEditMode() {
    this.getSearchStatusChange.emit(false);
  }

  onSubmit({value, valid}) {
    if (valid) {
      value.purge.hour = moment(value.purge.hour).format('HH:mm');
      value.purge.auto = Boolean(Number(value.purge.auto));
      value.purge.duration = value.purge.duration * 1000;
      value.purge.inactive_period = value.purge.inactive_period * 1000 * 3600;

      value.backwash.time = moment(value.backwash.time).format('HH:mm');
      value.backwash.auto = Boolean(Number(value.backwash.auto));
      value.backwash.duration = value.backwash.duration * 1000;

      value.splashpad.auto_startup = Boolean(Number(value.splashpad.auto_startup));

      value.communication.dhcp_on = Boolean(Number(value.communication.dhcp_on));

      value.water_circuit.bypass_delay = value.water_circuit.bypass_delay * 1000;
      value.water_circuit.rain_diverter_delay = value.water_circuit.rain_diverter_delay * 1000;
      value.water_circuit.elevation_delay = value.water_circuit.elevation_delay * 1000;
      value.water_circuit.ch_alarm_delay = value.water_circuit.ch_alarm_delay * 1000;
      value.water_circuit.uv_alarm_delay = value.water_circuit.uv_alarm_delay * 1000;
      value.water_circuit.step_speed_delay = value.water_circuit.step_speed_delay * 1000;
      value.water_circuit.any_fs_alarm_delay = value.water_circuit.any_fs_alarm_delay * 1000;
      value.water_circuit.feature_pump_on_delay = value.water_circuit.feature_pump_on_delay * 1000;
      value.water_circuit.filter_fs_alarm_delay = value.water_circuit.filter_fs_alarm_delay * 1000;
      value.water_circuit.filter_fs_start_delay = value.water_circuit.filter_fs_start_delay * 1000;
      value.water_circuit.feature_fs_start_delay = value.water_circuit.feature_fs_start_delay * 1000;

      this.inProgress = true;

      this.comm.checkSettings(this.parkId, this.id, value).then((res) => {
        this.updateSettings(value);
      }).catch((err) => {
        this.inProgress = false;

        if (typeof err === 'number' && (err === 1 || err === 2)) {
          const msg = (err === 1)
            ? 'Maestro Pro and GUI needs to restart, continue?'
            : 'The controller needs to reboot, continue?';

          if (confirm(msg)) {
            this.updateSettings(value);
          } else {
            this.inProgress = false;
          }
        } else {
          alert(err);
        }
      });
    }
  }

  private updateSettings(value) {
    this.comm.updateSettings(this.parkId, this.id, value).then((res) => {
      this.inProgress = false;
      this.setEditMode();
    }).catch((err) => {
      this.inProgress = false;

      if (typeof err === 'number' && (err === 1 || err === 2)) {
        const msg = (err === 1)
          ? 'Settings updated, Maestro Pro and GUI will restart'
          : 'Settings updated, the controller will reboot';

        this.setEditMode();

        alert(msg);
      } else {
        alert(err);
      }
    });
  }

  private getWc() {
    if (this.settings.hasOwnProperty('waterCircuit')) {
      return {
        bypass_delay: this.settings.waterCircuit.bypassDelay / 1000,
        mode: this.settings.waterCircuit.mode,
        rain_diverter_delay: this.settings.waterCircuit.rainDiverterDelay / 1000,
        y_clean_dp: this.settings.waterCircuit.yCleanDp,
        elevation_delay: this.settings.waterCircuit.elevationDelay / 1000,
        max_fast_valves_per_sec: this.settings.waterCircuit.maxFastValvesPerSec,
        max_normal_valves_per_sec: this.settings.waterCircuit.maxNormalValvesPerSec,
        y_dirty_dp: this.settings.waterCircuit.yDirtyDp,
        ch_full_acid: this.settings.waterCircuit.chFullAcid,
        max_conso: this.settings.waterCircuit.maxConso,
        ch_empty_acid: this.settings.waterCircuit.chEmptyAcid,
        sand_clean_dp: this.settings.waterCircuit.sandCleanDp,
        sand_dirty_dp: this.settings.waterCircuit.sandDirtyDp,
        ch_alarm_delay: this.settings.waterCircuit.chAlarmDelay / 1000,
        uv_alarm_delay: this.settings.waterCircuit.uvAlarmDelay / 1000,
        wind_max_speed: this.settings.waterCircuit.windMaxSpeed,
        wind_mid_speed: this.settings.waterCircuit.windMidSpeed,
        ch_full_chlorine: this.settings.waterCircuit.chFullChlorine,
        step_speed_delay: this.settings.waterCircuit.stepSpeedDelay / 1000,
        ch_empty_chlorine: this.settings.waterCircuit.chEmptyChlorine,
        any_fs_alarm_delay: this.settings.waterCircuit.anyFsAlarmDelay / 1000,
        feature_pump_on_delay: this.settings.waterCircuit.featurePumpOnDelay / 1000,
        filter_fs_alarm_delay: this.settings.waterCircuit.filterFsAlarmDelay / 1000,
        filter_fs_start_delay: this.settings.waterCircuit.filterFsStartDelay / 1000,
        feature_fs_start_delay: this.settings.waterCircuit.featureFsStartDelay / 1000,
      };
    } else {
      return {
        bypass_delay: '',
        mode: '',
        rain_diverter_delay: '',
        y_clean_dp: '',
        elevation_delay: '',
        max_fast_valves_per_sec: '',
        max_normal_valves_per_sec: '',
        y_dirty_dp: '',
        ch_full_acid: '',
        max_conso: '',
        ch_empty_acid: '',
        sand_clean_dp: '',
        sand_dirty_dp: '',
        ch_alarm_delay: '',
        uv_alarm_delay: '',
        wind_max_speed: '',
        wind_mid_speed: '',
        ch_full_chlorine: '',
        step_speed_delay: '',
        ch_empty_chlorine: '',
        any_fs_alarm_delay: '',
        feature_pump_on_delay: '',
        filter_fs_alarm_delay: '',
        filter_fs_start_delay: '',
        feature_fs_start_delay: '',
      };
    }
  }

  private getP() {
    if (this.settings.hasOwnProperty('purge')) {
      return {
        duration: this.settings.purge.duration / 1000,
        inactive_period: this.settings.purge.inactivePeriod / 1000 / 3600,
        hour: moment(this.settings.purge.hour, 'HH:mm').toISOString(),
        rain_diverter: this.settings.purge.rainDiverter,
        auto: Number(Boolean(this.settings.purge.auto)),
        bypass: this.settings.purge.bypass,
      };
    } else {
      return {
        duration: '',
        inactive_period: '',
        hour: '',
        rain_diverter: '',
        auto: '',
        bypass: '',
      };
    }
  }

  private getB() {
    if (this.settings.hasOwnProperty('backwash')) {
      return {
        duration: this.settings.backwash.duration / 1000,
        time: moment(this.settings.backwash.time, 'HH:mm').toISOString(),
        rain_diverter: this.settings.backwash.rainDiverter,
        bypass: this.settings.backwash.bypass,
        auto: Number(Boolean(this.settings.backwash.auto)),
      };
    } else {
      return {
        duration: '',
        time: '',
        rain_diverter: '',
        bypass: '',
        auto: '',
      };
    }
  }

  private getS() {
    if (this.settings.hasOwnProperty('splashpad')) {
      return {
        auto_startup: Number(Boolean(this.settings.splashpad.autoStartup)),
      };
    } else {
      return {
        auto_startup: '',
      };
    }
  }

  private getR() {
    if (this.settings.hasOwnProperty('regional')) {
      return {
        language: this.settings.regional.language,
        units: this.settings.regional.units,
        time_format: this.settings.regional.timeFormat,
      };
    } else {
      return {
        language: '',
        units: '',
        time_format: '',
      };
    }
  }

  private getL() {
    if (this.settings.hasOwnProperty('led')) {
      return {
        max_level: this.settings.led.maxLevel,
        step_level: this.settings.led.stepLevel,
        step_delay_ticks: this.settings.led.stepDelayTicks,
      };
    } else {
      return {
        max_level: '',
        step_level: '',
        step_delay_ticks: '',
      };
    }
  }

  private getC() {
    if (this.settings.hasOwnProperty('communication')) {
      return {
        dhcp_on: Number(Boolean(this.settings.communication.dhcpOn)),
        gsm_apn: this.settings.communication.gsmApn,
        gsm_pin: this.settings.communication.gsmPin,
        gsm_user: this.settings.communication.gsmUser,
        wlan_type: this.settings.communication.wlanType,
        gsm_password: this.settings.communication.gsmPassword,
        ch_ip_address: this.settings.communication.chIpAddress,
        maestro_ip_address: this.settings.communication.maestroIpAddress,
      };
    } else {
      return {
        dhcp_on: '',
        gsm_apn: '',
        gsm_pin: '',
        gsm_user: '',
        wlan_type: '',
        gsm_password: '',
        ch_ip_address: '',
        maestro_ip_address: '',
      };
    }
  }

  ngOnInit() {
    this.settingsForm.setValue({
      water_circuit: this.getWc(),
      purge: this.getP(),
      backwash: this.getB(),
      splashpad: this.getS(),
      regional: this.getR(),
      led: this.getL(),
      communication: this.getC(),
    });
  }

  purgeDurationValidation() {
    const c = this.settingsForm.get('purge').get('duration');

    return c.invalid && (c.dirty || c.touched);
  }

  purgePeriodValidation() {
    const c = this.settingsForm.get('purge').get('inactive_period');

    return c.invalid && (c.dirty || c.touched);
  }

  purgeHourValidation() {
    const c = this.settingsForm.get('purge').get('hour');

    return c.invalid && (c.dirty || c.touched);
  }

  backwashDurationValidation() {
    const c = this.settingsForm.get('backwash').get('duration');

    return c.invalid && (c.dirty || c.touched);
  }

  ledMaxLevelValidation() {
    const c = this.settingsForm.get('led').get('max_level');

    return c.invalid && (c.dirty || c.touched);
  }

  ledStepLevelValidation() {
    const c = this.settingsForm.get('led').get('step_level');

    return c.invalid && (c.dirty || c.touched);
  }

  ledStepDelayTicksValidation() {
    const c = this.settingsForm.get('led').get('step_delay_ticks');

    return c.invalid && (c.dirty || c.touched);
  }

  communicationDhcpOnValidation() {
    const c = this.settingsForm.get('communication').get('dhcp_on');

    return c.invalid && (c.dirty || c.touched);
  }

  communicationWlanTypeValidation() {
    const c = this.settingsForm.get('communication').get('wlan_type');

    return c.invalid && (c.dirty || c.touched);
  }

  communicationGsmApnValidation() {
    const c = this.settingsForm.get('communication').get('gsm_apn');

    return c.invalid && (c.dirty || c.touched);
  }

  communicationGsmPinValidation() {
    const c = this.settingsForm.get('communication').get('gsm_pin');

    return c.invalid && (c.dirty || c.touched);
  }

  communicationGsmUserValidation() {
    const c = this.settingsForm.get('communication').get('gsm_user');

    return c.invalid && (c.dirty || c.touched);
  }

  communicationGsmPasswordValidation() {
    const c = this.settingsForm.get('communication').get('gsm_password');

    return c.invalid && (c.dirty || c.touched);
  }

  backwashTimeValidation() {
    const c = this.settingsForm.get('backwash').get('time');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitBypassDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('bypass_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitAnyFsAlarmDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('any_fs_alarm_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitRainDiverterDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('rain_diverter_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitStepSpeedDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('step_speed_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitUvAlarmDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('uv_alarm_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitFeaturePumpOnDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('feature_pump_on_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitFeatureFsStartDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('feature_fs_start_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitFilterFsAlarmDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('filter_fs_alarm_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitFilterFsStartDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('filter_fs_start_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitMaxFastValvesPerSecValidation() {
    const c = this.settingsForm.get('water_circuit').get('max_fast_valves_per_sec');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitMaxNormalValvesPerSecValidation() {
    const c = this.settingsForm.get('water_circuit').get('max_normal_valves_per_sec');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitchEmptyAcidValidation() {
    const c = this.settingsForm.get('water_circuit').get('ch_empty_acid');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitChFullAcidValidation() {
    const c = this.settingsForm.get('water_circuit').get('ch_full_acid');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitChEmptyChlorineValidation() {
    const c = this.settingsForm.get('water_circuit').get('ch_empty_chlorine');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitChFullChlorineValidation() {
    const c = this.settingsForm.get('water_circuit').get('ch_full_chlorine');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitChAlarmDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('ch_alarm_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitElevationDelayValidation() {
    const c = this.settingsForm.get('water_circuit').get('elevation_delay');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitWaterConsumptionValidation() {
    const c = this.settingsForm.get('water_circuit').get('max_conso');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitModeValidation() {
    const c = this.settingsForm.get('water_circuit').get('mode');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitSandCleanDpValidation() {
    const c = this.settingsForm.get('water_circuit').get('sand_clean_dp');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitSandDirtyDpValidation() {
    const c = this.settingsForm.get('water_circuit').get('sand_dirty_dp');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitWindMaxSpeedValidation() {
    const c = this.settingsForm.get('water_circuit').get('wind_max_speed');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitWindMidSpeedValidation() {
    const c = this.settingsForm.get('water_circuit').get('wind_mid_speed');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitYCleanDpValidation() {
    const c = this.settingsForm.get('water_circuit').get('y_clean_dp');

    return c.invalid && (c.dirty || c.touched);
  }

  waterCircuitYDirtyDpValidation() {
    const c = this.settingsForm.get('water_circuit').get('y_dirty_dp');

    return c.invalid && (c.dirty || c.touched);
  }
}



// WEBPACK FOOTER //
// ./src/app/dashboard/forms/park-settings-form/park-settings-form.component.ts